/**
 * @brisa/shared/observability/clarity
 *
 * Helper canônico para emissão de eventos Microsoft Clarity (window.clarity).
 *
 * Convenções:
 *   - No-op silencioso no server.
 *   - No-op silencioso quando consent não foi concedido ou Clarity Project ID está ausente.
 *   - `env` é incluído automaticamente em todo evento (ADR-014).
 *   - Em DEV/PRD com consent + projectId presentes, dispara `window.clarity('event', name)`
 *     e `window.clarity('set', key, value)` (claritySet).
 *   - Em LOCAL/CI nunca dispara (fail-closed via shouldInjectClarity).
 *
 * Uso (client-side apenas):
 *   import { clarityEvent, claritySet } from '@brisa/shared/observability';
 *   clarityEvent('Signup Started', { provider: 'google', ... });
 *   claritySet('user_type', 'visitor');
 */
import { shouldInjectClarity } from './analytics';

declare global {
  interface Window {
    clarity?: (
      command: 'event' | 'set' | 'identify' | 'consent' | 'upgrade',
      ...args: unknown[]
    ) => void;
  }
}

interface EnvSnapshot {
  env: string;
  projectId: string | undefined | null;
}

function readEnvSnapshot(): EnvSnapshot {
  if (typeof window === 'undefined') {
    return { env: 'server', projectId: null };
  }
  const env = (process.env.NEXT_PUBLIC_BRISA_ENV as string | undefined) ?? 'local';
  const projectId =
    (process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID as string | undefined) ?? null;
  return { env, projectId };
}

function readConsentGranted(): boolean {
  if (typeof document === 'undefined') return false;
  const cookies = document.cookie.split(';').map((c) => c.trim());
  const consentCookie = cookies.find((c) => c.startsWith('brisa_consent='));
  if (consentCookie) {
    return consentCookie.endsWith('=granted');
  }
  return true;
}

function clarityActive(): boolean {
  if (typeof window === 'undefined') return false;
  const snapshot = readEnvSnapshot();
  const consentGranted = readConsentGranted();
  if (
    !shouldInjectClarity({
      env: snapshot.env,
      consentGranted,
      projectId: snapshot.projectId,
    })
  ) {
    return false;
  }
  return typeof window.clarity === 'function';
}

/**
 * Emite um evento Clarity via window.clarity('event', name).
 *
 * NB: Clarity API só aceita `name` (string) — `props` adicionais são propagadas
 * via `claritySet(key, value)` para serem visíveis nos session replays/segments.
 */
export function clarityEvent(name: string, props: Record<string, unknown> = {}): void {
  if (!clarityActive()) return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    window.clarity!('event', name);
    // Adicionar env como tag de sessão (ADR-014).
    const snapshot = readEnvSnapshot();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    window.clarity!('set', 'env', snapshot.env);
    // Propaga props simples (string/number/boolean) como tags de sessão.
    for (const [key, value] of Object.entries(props)) {
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        window.clarity!('set', key, String(value));
      }
    }
  } catch {
    // Silenciar — telemetria nunca quebra UX.
  }
}

/**
 * Define uma tag de sessão Clarity (key/value), útil para segmentar replays
 * por persona, locale, user_type etc.
 */
export function claritySet(key: string, value: string | number | boolean): void {
  if (!clarityActive()) return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    window.clarity!('set', key, String(value));
  } catch {
    // Silenciar.
  }
}
